import { decorateReward } from '@/common/reward/linkable';
import { StringUtils } from '@/common/utils/stringUtils';
import { useBaseListView } from '@/common/view/useBaseListView';
import { useCrudActions } from '@/common/view/useCrudActions';
import { LiveopsRewardType } from '@/modules/liveops/item/types';
import type { LiveopsMilestoneReward } from '@/modules/liveops/milestone/types';
import { useLiveopsReward } from '@/modules/liveops/shared/reward';
import { liveopsPointService } from '@/services/liveops';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';

export function useLiveopsMilestoneList() {
    const route = useRoute();
    const { executeAction, confirmAndRemove } = useCrudActions();
    const { fetchLinkableRewards, linkableRewards } = useLiveopsReward();

    const itemInfo = reactive({
        id: StringUtils.toNumber(route.params.itemId),
        rewardType: LiveopsRewardType.Unknown,
        canEditMilestone: false
    });

    const loadAndSetLiveopsItem = executeAction({
        canExecute: () => itemInfo.id != null,
        request: () => liveopsPointService.item.get(itemInfo.id),
        onSuccess: ({ data }) => {
            itemInfo.rewardType = data.rewardType;
            itemInfo.canEditMilestone = data.canEditMilestone;
        }
    });

    const { list, total, fetchList } = useBaseListView({
        request: liveopsPointService.milestone.list,
        paramsBuilder: () => ({ itemId: itemInfo.id }),
        hooks: {
            loadInitialData: async () => await Promise.all([fetchLinkableRewards(), loadAndSetLiveopsItem()]),
            transformResponse: (list: LiveopsMilestoneReward[]) =>
                list.map(milestone => decorateReward(milestone, linkableRewards.value))
        }
    });

    const actions = {
        remove: confirmAndRemove({
            request: (milestoneIndex: number) => liveopsPointService.milestone.remove(itemInfo.id, milestoneIndex),
            onSuccess: () => fetchList()
        })
    };

    return { list, total, fetchList, itemInfo, actions };
}
